import { useEffect, useRef, useState } from "react";

const THRESHOLD = 80;
const MAX_PULL = 64;
const MIN_REFRESH_DURATION = 500;

export default function usePullToRefresh({
  onRefresh = () => {},
  disabled = false,
} = {}) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const touchStart = useRef(0);
  const containerRef = useRef(null);

  const handleTouchStart = (e) => {
    if (disabled) return;
    const containerEl = containerRef.current;
    const isAtTop = (window.scrollY === 0) && (containerEl?.scrollTop === 0);
    if (!isAtTop) {
      touchStart.current = 0;
      return;
    }
    touchStart.current = e.touches[0]?.clientY || 0;
  };

  const handleTouchMove = (e) => {
    if (disabled || !touchStart.current || isRefreshing) return;
    const containerEl = containerRef.current;
    const isAtTop = (window.scrollY === 0) && (containerEl?.scrollTop === 0);
    if (!isAtTop) {
      touchStart.current = 0;
      return;
    }

    const currentY = e.touches[0]?.clientY || 0;
    const diff = currentY - touchStart.current;

    if (diff > 0) {
      e.preventDefault();
      setPullDistance(Math.min(diff * 0.4, MAX_PULL));
    }
  };

  const handleTouchEnd = (e) => {
    if (disabled || !touchStart.current) {
      touchStart.current = 0;
      setPullDistance(0);
      return;
    }

    const currentY = e.changedTouches[0]?.clientY || 0;
    const diff = currentY - touchStart.current;
    touchStart.current = 0;

    const containerEl = containerRef.current;
    const isAtTop = (window.scrollY === 0) && (containerEl?.scrollTop === 0);
    if (diff > THRESHOLD && isAtTop) {
      setPullDistance(40);
      setIsRefreshing(true);
      Promise.all([
        onRefresh(),
        new Promise(resolve => setTimeout(resolve, MIN_REFRESH_DURATION))
      ]).finally(() => {
        setIsRefreshing(false);
        setPullDistance(0);
      });
    } else {
      setPullDistance(0);
    }
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [disabled, isRefreshing, onRefresh]);

  return {
    containerProps: {
      ref: containerRef,
    },
    isRefreshing,
    pullDistance,
  };
}
